import React, { useRef } from 'react';
import { Box, FormHelperText, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

export interface OptionChoix {
  valeur: string;
  libelle: string;
}

interface Props {
  options: OptionChoix[];
  valeur: string;
  onChange: (valeur: string) => void;
  /** Décrit le groupe pour les lecteurs d'écran. */
  ariaLabel: string;
  erreur?: string;
  /** Désactive l'effet de survol, réservé au pointeur. */
  reduce?: boolean;
}

/**
 * Choix unique sous forme de cartes cliquables, en remplacement d'un Select.
 * Sur un écran de 360 px, dérouler un menu pour cocher « Marié(e) » coûte deux
 * gestes et masque le reste du formulaire ; ici tout est visible d'un coup.
 *
 * Ce sont des Box cliquables : sans role/tabIndex/flèches, elles seraient
 * totalement invisibles au clavier et aux lecteurs d'écran.
 */
const ChoixCartes: React.FC<Props> = ({ options, valeur, onChange, ariaLabel, erreur, reduce }) => {
  const refs = useRef<Array<HTMLDivElement | null>>([]);

  const indexActif = Math.max(
    0,
    options.findIndex((o) => o.valeur === valeur)
  );

  const surTouche = (e: React.KeyboardEvent, index: number) => {
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      onChange(options[index].valeur);
      return;
    }
    const avance =
      e.key === 'ArrowRight' || e.key === 'ArrowDown'
        ? 1
        : e.key === 'ArrowLeft' || e.key === 'ArrowUp'
        ? -1
        : 0;
    if (avance === 0) return;
    e.preventDefault();
    const suivant = (index + avance + options.length) % options.length;
    onChange(options[suivant].valeur);
    refs.current[suivant]?.focus();
  };

  return (
    <Box>
      <Box
        role="radiogroup"
        aria-label={ariaLabel}
        sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}
      >
        {options.map((option, index) => {
          const choisi = option.valeur === valeur;
          return (
            <Box
              key={option.valeur}
              ref={(el: HTMLDivElement | null) => {
                refs.current[index] = el;
              }}
              role="radio"
              aria-checked={choisi}
              tabIndex={index === indexActif ? 0 : -1}
              onClick={() => onChange(option.valeur)}
              onKeyDown={(e) => surTouche(e, index)}
              sx={{
                position: 'relative',
                flex: '1 1 auto',
                minWidth: 116,
                px: 2,
                py: 1.25,
                borderRadius: 3,
                cursor: 'pointer',
                userSelect: 'none',
                border: choisi ? '2px solid #2e7d32' : `2px solid ${erreur ? '#d32f2f' : '#e0e0e0'}`,
                backgroundColor: choisi ? 'rgba(46,125,50,0.06)' : 'background.paper',
                transition: reduce ? 'none' : 'all 250ms cubic-bezier(0.4,0,0.2,1)',
                '&:hover': reduce ? {} : { borderColor: '#2e7d32', transform: 'translateY(-2px)' },
                '&:focus-visible': { outline: '2px solid #2e7d32', outlineOffset: 2 },
              }}
            >
              <Typography
                sx={{
                  fontSize: '0.9rem',
                  fontWeight: choisi ? 700 : 500,
                  color: choisi ? '#2e7d32' : '#555',
                  pr: choisi ? 3 : 0,
                }}
              >
                {option.libelle}
              </Typography>
              {choisi && (
                <CheckCircleIcon
                  sx={{ position: 'absolute', top: 10, right: 10, fontSize: 18, color: '#2e7d32' }}
                />
              )}
            </Box>
          );
        })}
      </Box>
      {erreur && <FormHelperText error sx={{ ml: 1.5, mt: 0.75 }}>{erreur}</FormHelperText>}
    </Box>
  );
};

export default ChoixCartes;
